import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, X, Search, Check } from "lucide-react";

export interface MultiSelectProps {
  options: any[];
  // For multi-select mode
  selectedValues?: (string | number)[];
  // For single-select mode
  selectedValue?: string | number | null;
  onChange: (
    selectedValues: (string | number)[] | string | number | null
  ) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  maxSelectedDisplay?: number;
  disabled?: boolean;
  className?: string;
  dropdownClassName?: string;
  showSearch?: boolean;
  clearable?: boolean;
  labelKey?: string;
  valueKey?: string;
  multiple?: boolean; // New prop to control single/multi select
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options = [],
  selectedValues = [],
  selectedValue = null,
  onChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search options...",
  maxSelectedDisplay = 0,
  disabled = false,
  className = "",
  dropdownClassName = "",
  labelKey = "label",
  valueKey = "value",
  showSearch = true,
  clearable = true,
  multiple = true, // Default to multi-select for backward compatibility
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Get current selected values based on mode
  const getCurrentSelectedValues = (): (string | number)[] => {
    if (multiple) {
      return selectedValues;
    } else {
      return selectedValue !== null ? [selectedValue] : [];
    }
  };

  // Filter options based on search term using labelKey
  const filteredOptions = options.filter((option) =>
    option[labelKey]?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle option selection for both single and multi-select
  const handleOptionSelect = (optionValue: string | number) => {
    if (multiple) {
      // Multi-select logic
      const isSelected = selectedValues.includes(optionValue);
      const newSelectedValues = isSelected
        ? selectedValues.filter((value) => value !== optionValue)
        : [...selectedValues, optionValue];
      onChange(newSelectedValues);
    } else {
      // Single-select logic
      const newValue = selectedValue === optionValue ? null : optionValue;
      onChange(newValue);
      setIsOpen(false); // Close dropdown after selection in single-select mode
    }
  };

  // Handle remove selected item
  const handleRemoveItem = (valueToRemove: string | number) => {
    if (multiple) {
      onChange(selectedValues.filter((value) => value !== valueToRemove));
    } else {
      onChange(null);
    }
  };

  // Clear all selections
  const handleClearAll = () => {
    if (multiple) {
      onChange([]);
    } else {
      onChange(null);
    }
  };

  // Get display text for selected items
  const getDisplayText = () => {
    const currentSelected = getCurrentSelectedValues();

    if (currentSelected.length === 0) return placeholder;

    if (!multiple) {
      // Single select - show the selected option's label
      const selectedOption = options.find(
        (opt) => opt[valueKey] === selectedValue
      );
      return selectedOption ? selectedOption[labelKey] : placeholder;
    }

    return `${currentSelected.length} items selected`;
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (
          isOpen &&
          focusedIndex >= 0 &&
          focusedIndex < filteredOptions.length
        ) {
          handleOptionSelect(filteredOptions[focusedIndex][valueKey]);
        } else {
          setIsOpen(!isOpen);
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setFocusedIndex((prev) =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (isOpen) {
          setFocusedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
        break;
      case "Escape":
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, showSearch]);

  // Reset focused index when search term changes
  useEffect(() => {
    setFocusedIndex(-1);
  }, [searchTerm]);

  // Add custom scrollbar CSS with your brand colors
  useEffect(() => {
    const styleId = 'multiselect-scrollbar-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .multiselect-options-list::-webkit-scrollbar {
          width: 6px;
        }
        .multiselect-options-list::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        .multiselect-options-list::-webkit-scrollbar-thumb {
          background: #22577a;
          border-radius: 3px;
        }
        .multiselect-options-list::-webkit-scrollbar-thumb:hover {
          background: #1a4862;
        }
        .multiselect-options-list {
          scrollbar-width: thin;
          scrollbar-color: #22577a #f1f5f9;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  const currentSelected = getCurrentSelectedValues();
  const hasSelection = currentSelected.length > 0;

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Selected Items Display */}
      <div
        className={`
          min-h-[42px] px-3 py-2 border border-gray-300 rounded-lg bg-white
          cursor-pointer transition-all duration-200 flex items-center gap-2 overflow-hidden
          ${
            disabled
              ? "opacity-50 cursor-not-allowed bg-gray-50"
              : "hover:border-gray-400 focus-within:ring-2"
          }
          ${isOpen ? "ring-2" : ""}
        `}
        style={{
          borderColor: isOpen ? '#22577a' : undefined,
          '--tw-ring-color': '#22577a33', // 20% opacity
        } as React.CSSProperties}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex-1 flex flex-wrap gap-1">
          {multiple &&
          hasSelection &&
          currentSelected.length <= maxSelectedDisplay ? (
            currentSelected.map((value) => {
              const option = options.find((opt) => opt[valueKey] === value);
              return option ? (
                <span
                  key={value}
                  className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded-md text-white"
                  style={{ backgroundColor: '#2a9d8f' }}
                >
                  {option[labelKey]}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveItem(value);
                      }}
                      className="rounded-full p-0.5 transition-colors hover:bg-black/20"
                    >
                      <X size={14} />
                    </button>
                  )}
                </span>
              ) : null;
            })
          ) : (
            <span
              className={`text-sm ${
                !hasSelection ? "text-gray-500" : "text-gray-900"
              }`}
            >
              {getDisplayText()}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {clearable && hasSelection && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={18} />
            </button>
          )}
          <ChevronDown
            size={18}
            className={`text-gray-400 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`
          absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 
          rounded-lg shadow-lg z-50 max-h-60 overflow-hidden ${dropdownClassName}
        `}
        >
          {/* Search Input */}
          {showSearch && (
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2"
                  style={{ 
                    borderColor: searchTerm ? '#22577a' : undefined,
                    '--tw-ring-color': '#22577a33'
                  } as React.CSSProperties}
                />
              </div>
            </div>
          )}

          {/* Options List with Custom Scrollbar */}
          <div className="max-h-48 overflow-y-auto overflow-x-hidden py-auto w-auto multiselect-options-list">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                No options found
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = currentSelected.includes(option[valueKey]);
                const isFocused = index === focusedIndex;

                return (
                  <div
                    key={option[valueKey]}
                    className={`
                      px-3 py-2 cursor-pointer flex items-center justify-between text-sm
                      transition-colors select-none
                      ${option.disabled ? "opacity-50 cursor-not-allowed" : ""}
                      ${isFocused && !option.disabled ? "bg-gray-100" : ""}
                      ${
                        isSelected
                          ? "hover:bg-gray-50"
                          : "hover:bg-gray-50"
                      }
                    `}
                    style={{
                      backgroundColor: isSelected ? '#2a9d8f15' : undefined, // 8% opacity
                      color: isSelected ? '#22577a' : undefined,
                    }}
                    onClick={() =>
                      !option.disabled && handleOptionSelect(option[valueKey])
                    }
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span>{option[labelKey]}</span>
                    {isSelected && (
                      <Check size={16} style={{ color: '#22577a' }} />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
